import Table from '@/components/TableComponents/Table';
import useI18n from '@/hooks/useI18n';
import { useState, useContext, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import BillingModal from '../BillingModal';
import { useCampaignCreatorListQuery } from '@/hooks/queries/useAnalyticsQueries';
import { format } from 'date-fns';
import { UserContext } from '@/context/UserContext';
import { formatPlatforms } from '@/utils/platformLabels';
import type { DateFilteredData } from '@/hooks/useDateRangeQueryParams';
import { formatMoney } from '@/utils/numberUtils';
import {
  CampaignCreator,
  SubmissionRow,
  deriveSubmissionRows,
} from './groupedSubmissionRows';

const PAGE_SIZE = 12;

// Demo creators shown only to the dummy/demo user, since the live
// creator-list API returns nothing for that account. Grouped shape mirrors
// the PROD-181 creatorList response; userIds match DUMMY_BY_USER_ID in
// UserDetail.tsx so clicking a row opens the right creator. Yenish carries
// two submissions across both platforms to demo the merged row.
const dummyCreatorList: CampaignCreator[] = [
  {
    userId: '670ceb2eab89dc9c74f386fb',
    firstName: 'Rahul',
    lastName: 'Chauhan',
    profileImage: null,
    profileUrl: null,
    userName: null,
    latestPlatform: 'instagram',
    platforms: ['instagram'],
    totalCashback: 25,
    submissionCount: 1,
    submissions: [
      {
        submissionId: '6a154e121727391fb520bc71',
        submittedOn: '2025-05-10T10:30:00Z',
        platform: 'instagram',
        invoice:
          'https://linkfluencer.s3.eu-west-3.amazonaws.com/invoice/1779276893735-mbus6h.png',
        cashback: 25,
      },
    ],
  },
  {
    userId: '68e3ba511dd2e9ad6c69c72c',
    firstName: 'Yenish',
    lastName: 'Radadiya',
    profileImage: null,
    profileUrl: null,
    userName: null,
    latestPlatform: 'instagram',
    platforms: ['instagram', 'tiktok'],
    totalCashback: 20,
    submissionCount: 2,
    submissions: [
      {
        submissionId: '68e91cbd7e3c7144fe37d9ee',
        submittedOn: '2025-10-10T14:48:29.134Z',
        platform: 'instagram',
        invoice:
          'https://linkfluencer.s3.eu-west-3.amazonaws.com/invoice/1760107678981-xjiw3q.png',
        cashback: 11,
      },
      {
        submissionId: '68e91cbd7e3c7144fe37d9ef',
        submittedOn: '2025-09-28T09:15:00Z',
        platform: 'tiktok',
        invoice: null,
        cashback: 9,
      },
    ],
  },
];

interface CampaignSubmissionsTableProps {
  campaign: any;
  dateFilteredData: DateFilteredData;
}

const CampaignSubmissionsTableComponent = ({
  campaign,
  dateFilteredData,
}: CampaignSubmissionsTableProps) => {
  const { t } = useI18n();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState<string>('');
  const [page, setPage] = useState(1);
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { isDummyUser } = userContext;

  const handleModal = (url?: string) => {
    if (url) setInvoiceUrl(url);
    setIsModalOpen(!isModalOpen);
  };

  const { data: creatorList = [] } = useCampaignCreatorListQuery(
    campaign?._id || '',
    !!campaign?._id,
  );

  const creators: CampaignCreator[] = isDummyUser
    ? dummyCreatorList
    : creatorList;

  const rows = useMemo(
    () =>
      deriveSubmissionRows(
        creators,
        dateFilteredData.startDate ?? null,
        dateFilteredData.endDate ?? null,
      ),
    [creators, dateFilteredData],
  );

  // New range can shrink the row set below the current page — snap back.
  useEffect(() => {
    setPage(1);
  }, [dateFilteredData]);

  const pagedRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const detailsUrl = (row: SubmissionRow) =>
    `/user-detail?userId=${row.userId}&campaignId=${campaign?._id ?? ''}`;

  const columns = [
    {
      title: t('common.influencer'),
      dataIndex: 'influencer',
      key: 'influencer',
      width: '100%',
      render: (row: SubmissionRow) => (
        <Link
          className="flex items-center gap-2"
          to={detailsUrl(row)}
          state={{ fromMenu: 'campaign', fromTab: 'active' }}
        >
          <img
            src={row.avatarUrl ?? '/assets/profileImg.svg'}
            alt={'/assets/profileImg.svg'}
            className="w-[32px] h-[32px] rounded-full"
          />
          <span>{row.name}</span>
        </Link>
      ),
    },
    {
      title: t('common.platform'),
      dataIndex: 'platform',
      key: 'platform',
      width: '100%',
      render: (row: SubmissionRow) => (
        <Link
          to={detailsUrl(row)}
          state={{ fromMenu: 'campaign', fromTab: 'active' }}
        >
          <div>{formatPlatforms(row.platforms)}</div>
        </Link>
      ),
    },
    {
      title: t('common.submitted'),
      dataIndex: 'submittedDate',
      key: 'submittedDate',
      width: '100%',
      render: (row: SubmissionRow) => (
        <Link
          to={detailsUrl(row)}
          state={{ fromMenu: 'campaign', fromTab: 'active' }}
        >
          <div>
            {row.submittedOn
              ? format(new Date(row.submittedOn), 'dd/MM/yyyy')
              : '-'}
          </div>
        </Link>
      ),
    },
    {
      title: t('profile.status'),
      dataIndex: 'status',
      key: 'status',
      width: '100%',
      render: (row: SubmissionRow) => (
        <Link
          to={detailsUrl(row)}
          state={{ fromMenu: 'campaign', fromTab: 'active' }}
        >
          <span className="text-sm font-normal items-center px-2 py-1 rounded-full text-[#0FAF62] bg-[rgba(15,175,98,0.1)]">
            {`Paid - ${formatMoney(row.totalCashback, { cents: true })}`}
          </span>
        </Link>
      ),
    },
    {
      title: t('common.action'),
      dataIndex: 'action',
      key: 'action',
      width: '100%',
      render: (row: SubmissionRow) =>
        row.invoice ? (
          <div>
            <img
              src="/assets/eye.svg"
              alt={String(t('campaign.actions.view'))}
              style={{ cursor: 'pointer' }}
              onClick={() => handleModal(row.invoice ?? undefined)}
            />
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <div className="border border-[#E5E9EB] rounded-3xl p-[24px] mb-4">
        <div className="text-[#121111] text-lg font-medium pb-[24px]">
          {t('common.campaignSubmissions')}
        </div>
        {rows.length === 0 ? (
          <div className="flex items-center justify-center min-h-[50px]">
            <span className="text-xl font-medium text-[#9B919D]">
              {String(t('common.noData'))}
            </span>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={pagedRows}
            pagination={{
              total: rows.length,
              current: page,
              pageSize: PAGE_SIZE,
              onChange: (nextPage: number) => setPage(nextPage),
            }}
          />
        )}
      </div>
      <BillingModal
        isModalOpen={isModalOpen}
        handleModal={handleModal}
        invoiceUrl={invoiceUrl}
      />
    </>
  );
};

export default CampaignSubmissionsTableComponent;
